import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Plus, Trash2, Truck, Phone, DollarSign, Wallet, MinusCircle, MapPin } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDrivers = () => {
  const { getAuthHeaders } = useAuth();
  const { t, isRTL } = useLanguage();
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [financeDialog, setFinanceDialog] = useState({ open: false, driver: null, finance: null });
  const [payAmount, setPayAmount] = useState("");
  const [deductAmount, setDeductAmount] = useState("");
  const [deductReason, setDeductReason] = useState("");
  const [formData, setFormData] = useState({
    full_name: "", phone: "", vehicle_type: "", vehicle_number: "", username: "", password: "",
  });

  useEffect(() => { fetchDrivers(); }, []);

  const fetchDrivers = async () => {
    try {
      const res = await axios.get(`${API}/drivers`, { headers: getAuthHeaders() });
      setDrivers(res.data);
    } catch (error) {
      toast.error(t("failedToFetchDrivers"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/drivers`, formData, { headers: getAuthHeaders() });
      toast.success(t("driverAdded"));
      setDialogOpen(false);
      setFormData({ full_name: "", phone: "", vehicle_type: "", vehicle_number: "", username: "", password: "" });
      fetchDrivers();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("failedToAddDriver"));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("removeDriverConfirm"))) return;
    try {
      await axios.delete(`${API}/drivers/${id}`, { headers: getAuthHeaders() });
      toast.success(t("driverRemoved"));
      fetchDrivers();
    } catch (error) {
      toast.error(t("failedToRemoveDriver"));
    }
  };

  const openFinanceDialog = async (driver) => {
    try {
      const res = await axios.get(`${API}/drivers/${driver.id}/finance`, { headers: getAuthHeaders() });
      setFinanceDialog({ open: true, driver, finance: res.data });
      setPayAmount("");
      setDeductAmount("");
      setDeductReason("");
    } catch (error) {
      toast.error(isRTL ? "فشل في تحميل البيانات المالية" : "Failed to load finance data");
    }
  };

  const handlePay = async () => {
    if (!payAmount || parseFloat(payAmount) <= 0) {
      toast.error(isRTL ? "الرجاء إدخال مبلغ صحيح" : "Please enter a valid amount");
      return;
    }
    try {
      await axios.post(
        `${API}/drivers/${financeDialog.driver.id}/pay?amount=${payAmount}&payment_method=cash`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(isRTL ? "تم تسجيل الدفع بنجاح" : "Payment recorded successfully");
      openFinanceDialog(financeDialog.driver); // Refresh finance data
    } catch (error) {
      toast.error(error.response?.data?.detail || (isRTL ? "فشل في تسجيل الدفع" : "Failed to record payment"));
    }
  };

  const handleDeduct = async () => {
    if (!deductAmount || parseFloat(deductAmount) <= 0) {
      toast.error(isRTL ? "الرجاء إدخال مبلغ صحيح" : "Please enter a valid amount");
      return;
    }
    if (!deductReason.trim()) {
      toast.error(isRTL ? "الرجاء إدخال سبب الخصم" : "Please enter deduction reason");
      return;
    }
    try {
      await axios.post(
        `${API}/drivers/${financeDialog.driver.id}/deduct`,
        { amount: parseFloat(deductAmount), reason: deductReason },
        { headers: getAuthHeaders() }
      );
      toast.success(isRTL ? "تم تسجيل الخصم بنجاح" : "Deduction recorded successfully");
      openFinanceDialog(financeDialog.driver); // Refresh finance data
    } catch (error) {
      toast.error(error.response?.data?.detail || (isRTL ? "فشل في تسجيل الخصم" : "Failed to record deduction"));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-drivers">
      <div className="flex justify-between">
        <h2 className="text-xl font-heading font-semibold text-white">{t("drivers")} ({drivers.length})</h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#d4af37] text-[#0f172a]" data-testid="add-driver-btn">
              <Plus className="mr-2 h-4 w-4" /> {t("addDriver")}
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle className="font-heading">{t("addNewDriver")}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">{t("fullName")} *</Label>
                  <Input value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} required className="bg-slate-700 border-slate-600" />
                </div>
                <div>
                  <Label className="text-slate-300">{t("phone")} *</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} required className="bg-slate-700 border-slate-600" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">{t("vehicleType")}</Label>
                  <Input value={formData.vehicle_type} onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })} placeholder="Car, Motorcycle..." className="bg-slate-700 border-slate-600" />
                </div>
                <div>
                  <Label className="text-slate-300">{t("vehicleNumber")}</Label>
                  <Input value={formData.vehicle_number} onChange={(e) => setFormData({ ...formData, vehicle_number: e.target.value })} className="bg-slate-700 border-slate-600" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">{t("username")} *</Label>
                  <Input value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} required className="bg-slate-700 border-slate-600" />
                </div>
                <div>
                  <Label className="text-slate-300">{t("password")} *</Label>
                  <Input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required className="bg-slate-700 border-slate-600" />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-600">{t("cancel")}</Button>
                <Button type="submit" className="bg-[#d4af37] text-[#0f172a]" data-testid="save-driver-btn">{t("addDriver")}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-400">{t("loading")}</div>
          ) : drivers.length === 0 ? (
            <div className="p-8 text-center">
              <Truck className="w-12 h-12 mx-auto text-slate-500 mb-4" />
              <p className="text-slate-400">{t("noDriversYet")}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700">
                  <TableHead className="text-slate-300">{t("name")}</TableHead>
                  <TableHead className="text-slate-300">{t("phone")}</TableHead>
                  <TableHead className="text-slate-300">{t("vehicle")}</TableHead>
                  <TableHead className="text-slate-300">{isRTL ? "الموقع" : "Location"}</TableHead>
                  <TableHead className="text-slate-300">{t("deliveries")}</TableHead>
                  <TableHead className="text-slate-300">{t("status")}</TableHead>
                  <TableHead className="text-slate-300 text-right">{t("actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drivers.map((driver) => (
                  <TableRow key={driver.id} className="border-slate-700">
                    <TableCell className="text-white font-medium">{driver.full_name}</TableCell>
                    <TableCell className="text-slate-300">{driver.phone}</TableCell>
                    <TableCell className="text-slate-300">{driver.vehicle_type || "-"} {driver.vehicle_number || ""}</TableCell>
                    <TableCell className="text-slate-300">
                      {driver.current_location ? (
                        <a 
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(driver.current_location)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-400 hover:underline"
                        >
                          <MapPin className="h-3 w-3" />
                          <span className="truncate max-w-[100px]">{driver.current_location}</span>
                        </a>
                      ) : (
                        <span className="text-slate-500">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-white">{driver.total_deliveries || 0}</TableCell>
                    <TableCell>
                      <Badge className={driver.is_active ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}>
                        {driver.is_active ? t("active") : t("inactive")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => openFinanceDialog(driver)} 
                        className="text-[#d4af37] hover:text-[#d4af37]/80"
                        data-testid={`finance-driver-${driver.id}`}
                      >
                        <Wallet className="h-4 w-4" />
                      </Button>
                      <a href={`https://wa.me/${driver.phone?.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="icon" className="text-green-400 hover:text-green-300">
                          <Phone className="h-4 w-4" />
                        </Button>
                      </a>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(driver.id)} className="text-red-400 hover:text-red-300" data-testid={`delete-driver-${driver.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Finance Dialog */}
      <Dialog open={financeDialog.open} onOpenChange={(open) => setFinanceDialog({ ...financeDialog, open })}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Wallet className="h-5 w-5 text-[#d4af37]" />
              {isRTL ? "الحساب المالي للسائق" : "Driver Financial Account"}: {financeDialog.driver?.full_name}
            </DialogTitle>
          </DialogHeader>
          
          {financeDialog.finance && (
            <div className="space-y-6">
              {/* Balance Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-slate-700 border-slate-600">
                  <CardContent className="p-4 text-center">
                    <p className="text-slate-400 text-sm">{isRTL ? "إجمالي الأرباح" : "Total Earned"}</p>
                    <p className="text-2xl font-bold text-white">{financeDialog.finance.total_earned?.toFixed(2) || "0.00"}</p>
                    <p className="text-xs text-slate-500">{t("lyd")}</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-700 border-slate-600">
                  <CardContent className="p-4 text-center">
                    <p className="text-slate-400 text-sm">{isRTL ? "تم الدفع" : "Total Paid"}</p>
                    <p className="text-2xl font-bold text-green-400">{financeDialog.finance.total_paid?.toFixed(2) || "0.00"}</p>
                    <p className="text-xs text-slate-500">{t("lyd")}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-[#d4af37]/20 to-slate-700 border-[#d4af37]/30">
                  <CardContent className="p-4 text-center">
                    <p className="text-slate-400 text-sm">{isRTL ? "الرصيد المستحق" : "Balance Due"}</p>
                    <p className="text-2xl font-bold text-[#d4af37]">{financeDialog.finance.balance?.toFixed(2) || "0.00"}</p>
                    <p className="text-xs text-slate-500">{t("lyd")}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Pay Driver Section */}
              <Card className="bg-slate-700 border-slate-600">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-green-400" />
                    {isRTL ? "دفع للسائق" : "Pay Driver"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-2">
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={payAmount}
                      onChange={(e) => setPayAmount(e.target.value)}
                      placeholder={isRTL ? "المبلغ..." : "Amount..."}
                      className="bg-slate-600 border-slate-500 flex-1"
                      data-testid="pay-amount-input"
                    />
                    <Button 
                      onClick={handlePay}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="pay-driver-btn"
                    >
                      <DollarSign className="h-4 w-4 mr-1" />
                      {isRTL ? "دفع" : "Pay"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Deduct from Driver Section */}
              <Card className="bg-slate-700 border-slate-600">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <MinusCircle className="h-4 w-4 text-red-400" />
                    {isRTL ? "خصم من الحساب" : "Deduct from Account"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-2 space-y-3">
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      value={deductAmount}
                      onChange={(e) => setDeductAmount(e.target.value)}
                      placeholder={isRTL ? "المبلغ..." : "Amount..."}
                      className="bg-slate-600 border-slate-500 w-32"
                      data-testid="deduct-amount-input"
                    />
                    <Input
                      value={deductReason}
                      onChange={(e) => setDeductReason(e.target.value)}
                      placeholder={isRTL ? "سبب الخصم..." : "Deduction reason..."}
                      className="bg-slate-600 border-slate-500 flex-1"
                      data-testid="deduct-reason-input"
                    />
                    <Button 
                      onClick={handleDeduct}
                      variant="destructive"
                      className="bg-red-600 hover:bg-red-700"
                      data-testid="deduct-driver-btn"
                    >
                      <MinusCircle className="h-4 w-4 mr-1" />
                      {isRTL ? "خصم" : "Deduct"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Earnings/Deductions */}
              <Card className="bg-slate-700 border-slate-600">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{isRTL ? "سجل المعاملات" : "Transaction History"}</CardTitle>
                </CardHeader>
                <CardContent className="pt-2 max-h-[200px] overflow-y-auto">
                  {financeDialog.finance.earnings?.length > 0 ? (
                    <div className="space-y-2">
                      {financeDialog.finance.earnings.slice(0, 15).map((item) => (
                        <div key={item.id} className="flex items-center justify-between p-2 bg-slate-600/50 rounded">
                          <div>
                            <p className="text-sm text-white">
                              {item.type === "deduction" ? (
                                <span className="text-red-400">{isRTL ? "خصم" : "Deduction"}</span>
                              ) : (
                                <span className="text-green-400">{isRTL ? "أرباح توصيل" : "Delivery"}</span>
                              )}
                            </p>
                            <p className="text-xs text-slate-400">{item.description}</p>
                          </div>
                          <div className="text-right">
                            <p className={`font-bold ${item.amount < 0 ? 'text-red-400' : 'text-[#d4af37]'}`}>
                              {item.amount < 0 ? '' : '+'}{item.amount?.toFixed(2)} {t("lyd")}
                            </p>
                            <Badge className={item.status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>
                              {item.status === 'paid' ? (isRTL ? 'مدفوع' : 'Paid') : (isRTL ? 'معلق' : 'Pending')}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-400 py-4">
                      {isRTL ? "لا توجد معاملات بعد" : "No transactions yet"}
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDrivers;
